import React, { useState } from 'react';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { css } from '/imports/ui/css';
import { strings } from '/imports/strings';
import { projectManagementResTpoImport, projectManagementWipeRicProjects } from '/imports/api/projectManagement';
import { toastOnError } from '/imports/ui/utils';
import { DevToolSection } from '/imports/ui/DevToolSection';
import { FileInput } from '/imports/ui/FileInput';
import { FormField } from '/imports/ui/FormField';
import { Input } from '/imports/ui/Input';
import { Loading } from '/imports/ui/Loading';
import { Button } from '/imports/ui/Button';
import { ConfirmModal } from '/imports/ui/ConfirmModal';
import { maxSpreadsheetUploadBytes } from '/imports/api/files';

const DevToolResTpoImportStyled = styled.div`
  .field { margin-bottom: 0.75rem; max-width: 46rem; }
  label { display: block; margin-bottom: 0.25rem; font-weight: 500; }
  .field .input, .field input { width: 100%; box-sizing: border-box; }
  .meta-row { color: ${css.grey3}; font-size: 0.8rem; margin-top: 0.25rem; }
`;

/** Dev tool: seed the Residential Prepaid vertical from the Residential Pipeline Tracker xlsx. */
export const DevToolResTpoImport = () => {
  const [dealer, setDealer] = useState('');
  const [loading, setLoading] = useState(false);
  const [wiping, setWiping] = useState(false);
  const [confirmWipe, setConfirmWipe] = useState(false);
  const [result, setResult] = useState('');
  const busy = loading || wiping;
  const run = toastOnError(async (file: any) => {
    if (!file || busy) return;
    setLoading(true);
    setResult('');
    try {
      const res = await projectManagementResTpoImport.call({ file, dealer: dealer.trim() || undefined });
      setResult(JSON.stringify(res, null, 2));
      toast.success(strings.ResidentialTrackerImported(res.rows, res.created, res.updated, res.skipped));
    }
    finally {
      setLoading(false);
    }
  });
  const wipe = toastOnError(async (password: string) => {
    setWiping(true);
    setResult('');
    try {
      const res = await projectManagementWipeRicProjects.call({ lifecycleKey: 'res-tpo', password });
      setResult(JSON.stringify(res, null, 2));
      toast.success(strings.ResidentialProjectsWiped(res.projects, res.childRecords));
      setConfirmWipe(false);
    }
    finally {
      setWiping(false);
    }
  });
  return (
    <DevToolSection title={strings.ResidentialTrackerImport()} description={strings.ResidentialTrackerImportHelp()}>
      <DevToolResTpoImportStyled>
        <FormField field="dealer">
          <label>{strings.Dealer()}</label>
          <Input value={dealer} disabled={busy} onChange={(v) => setDealer(v || '')} />
          <div className="meta-row">{strings.ResidentialTrackerDealerHint()}</div>
        </FormField>
        <FileInput placeholder={loading ? strings.Importing() : strings.UploadResidentialTracker()} maxSize={maxSpreadsheetUploadBytes} onChange={(file) => run(file)} />
        <div style={{ marginTop: '0.75rem' }}>
          <Button className="secondary" disabled={busy} onClick={() => setConfirmWipe(true)}>{wiping ? strings.Wiping() : strings.WipeResidentialProjects()}</Button>
        </div>
      </DevToolResTpoImportStyled>
      {busy && <Loading caption={wiping ? strings.WipingResidentialProjects() : strings.ImportingTracker()} />}
      {!busy && result && <pre>{result}</pre> || null}
      {
        confirmWipe &&
        <ConfirmModal
          title={strings.WipeResidentialProjectsConfirm()}
          body={strings.WipeResidentialProjectsBody()}
          confirmText={strings.Wipe()}
          requirePassword
          onConfirm={(password) => wipe(password || '')}
          onClose={() => setConfirmWipe(false)}
        />
      }
    </DevToolSection>
  );
};
